import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "store";

import { Modal } from "components/Modal/Modal";
import Button from "components/buttons/Button";

import { closeModal } from "store/modals/slice";

import {
  getCustomWidgetItem,
  handleClearFields,
  createCustomWidget,
  updateCustomWidget,
  handleUpdateFields,
} from "store/cms/customized/slice";

import { NotificationTypes, showNotification } from "store/notifications/slice";

import { ILocaleData, ModalCustomizedTabs, ModalTypes } from "configs/types";

import ModalSkeleton, { ModalFooterSkeleton } from "components/global/Skeleton/ModalSkeleton/ModalSkeleton";
import { useFormErrors } from "helpers/useFormErrors";
import { handleApiError } from "helpers/handleApiError";
import {
  getChangedFields,
  normalizeOptionalText,
} from "helpers/getChangedFields";
import instance from "api/apiClient";
import { apiRoutes } from "configs/apiRoutes";
import LocalTab from "../globalTabs/LocalTab";

import CmsCustomizedMainTab from "./tabs/CmsCustomizedMainTab";
import CustomizedItemsTab from "./tabs/CmsCustomizedItemsTab";

import styles from "./CmsCustomizedEditModal.module.scss";
import { usePermissionHelper } from "helpers/usePermissionHelper";

const CmsCustomizedEditModal: React.FC = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const navigate = useNavigate();
  const location = useLocation();

  const [searchParams] = useSearchParams();

  const entityId = searchParams.get("entityId");

  const payload = useAppSelector((store) => store["cms-customized"].payload);

  const isEditing = Boolean(entityId);

  const isItemFetched = useAppSelector(
    (store) => store["cms-customized"].isItemFetched,
  );

  const isItemFetching = useAppSelector(
    (store) => store["cms-customized"].isItemFetching,
  );
  const isCustomWidgetItemsFetched = useAppSelector(
    (store) => store["cms-customized"].isCustomWidgetItemsFetched,
  );
  
  const geoList = useAppSelector((store) => store.settings.geoList);

  const permissions = usePermissionHelper();

  const hasSelectedGeo = Boolean(payload?.geo?.length);

  const hasMainData = Boolean(payload?.id);

  const formErrors = useFormErrors();

  const [tabs] = useState<ModalCustomizedTabs[]>([
    ModalCustomizedTabs.main,
    ModalCustomizedTabs.items,
    ModalCustomizedTabs.locales,
  ]);

  const [activeTab, setActiveTab] = useState<ModalCustomizedTabs>(
    ModalCustomizedTabs.main,
  );
  const [localeData, setLocaleData] = useState<ILocaleData>();
  const [initialLocaleData, setInitialLocaleData] = useState<ILocaleData>();
  const [localeReady, setLocaleReady] = useState(false);
  const [domainOptions, setDomainOptions] = useState([
    { value: "", label: "Default / Brand domain" },
  ]);

  useEffect(() => {
    if (entityId && !isItemFetched) {
      dispatch(getCustomWidgetItem(entityId));
    }
  }, [entityId, isItemFetched]);

  useEffect(() => {
    if (activeTab !== ModalCustomizedTabs.locales) return;

    let cancelled = false;
    instance
      .post(apiRoutes.mirrorList)
      .then((result) => {
        if (cancelled) return;
        const mirrors = result.data?.items ?? [];
        setDomainOptions([
          { value: "", label: "Default / Brand domain" },
          ...mirrors
            .filter(
              (item: { status: string; domain: string }) =>
                ["created", "activated"].includes(item.status) && item.domain,
            )
            .map((item: { domain: string; isDefault: number }) => ({
              value: item.domain,
              label: `${item.domain}${item.isDefault === 1 ? " (Main)" : ""}`,
            })),
        ]);
      })
      .catch((error) => {
        if (!cancelled) handleApiError({ error, t, dispatch });
      });

    return () => {
      cancelled = true;
    };
  }, [activeTab, entityId, dispatch, t]);

  const handleCloseClick = () => {
    navigate(location.pathname);

    dispatch(closeModal());

    dispatch(handleClearFields());
  };

  const handleSetLocale = async () => {
    if (!localeData || !localeReady || !entityId) return;

    const { geo, locale, domain } = localeData;
    const textChanges = getChangedFields(
      initialLocaleData,
      localeData,
      ["title"],
      { title: normalizeOptionalText },
    );
    if (textChanges.title === null) {
      textChanges.title = "";
    }

    try {
      await instance.put(`${apiRoutes.setCustomizedItemLocale}/${entityId}`, {
        geo,
        locale,
        ...(domain ? { domain } : {}),
        ...textChanges,
      });
      setInitialLocaleData(localeData);
      dispatch(
        showNotification({
          title: t("success"),
          message: t("entityUpdateSuccess"),
          type: NotificationTypes.success,
        }),
      );
    } catch (error) {
      handleApiError({
        error,
        t,
        dispatch,
        formErrors,
      });
    }
  };

  const handleApply = async () => {
    if (activeTab === ModalCustomizedTabs.locales) {
      await handleSetLocale();
      return;
    }

    formErrors.resetErrors();

    if (!payload.geo?.length) {
      dispatch(
        showNotification({
          title: t("warning"),
          message: t("selectGeoFirst"),
          type: NotificationTypes.warning,
        }),
      );

      return;
    }

    try {
      if (isEditing) {
        await dispatch(updateCustomWidget(payload)).unwrap();

        dispatch(
          showNotification({
            title: t("success"),
            message: t("entityUpdateSuccess"),
            type: NotificationTypes.success,
          }),
        );
      } else {
        const newId = await dispatch(createCustomWidget(payload)).unwrap();

        dispatch(
          handleUpdateFields({
            ...payload,
            id: newId,
          }),
        );

        dispatch(
          showNotification({
            title: t("success"),
            message: t("entityCreateSuccess"),
            type: NotificationTypes.success,
          }),
        );

        navigate(
          `${location.pathname}?activeModal=${ModalTypes.customized}&entityId=${newId}`,
        );

        return;
      }
    } catch (error) {
      handleApiError({
        error,
        t,
        dispatch,
        formErrors,
      });
    }
  };

  const renderTab = () => {
    if (activeTab === ModalCustomizedTabs.main) {
      return (
        <CmsCustomizedMainTab
          fieldErrors={formErrors.errors}
          clearFieldError={formErrors.clearFieldError}
          readOnly={!permissions?.update}
        />
      );
    }

    if (activeTab === ModalCustomizedTabs.items) {
      return (
        <CustomizedItemsTab
          readOnly={!permissions?.update}
          fieldErrors={formErrors.errors}
          clearFieldError={formErrors.clearFieldError}
          resetErrors={formErrors.resetErrors}
        />
      );
    }

    if (activeTab === ModalCustomizedTabs.locales) {
      return (
        <LocalTab
          id={entityId ?? undefined}
          currentData={localeData}
          handleData={setLocaleData}
          onInitialData={setInitialLocaleData}
          domainOptions={domainOptions}
          onReadyChange={setLocaleReady}
          onLoadError={(error) => handleApiError({ error, t, dispatch })}
          url={apiRoutes.getCustomizedItemLocale}
          titleOnly
          fieldErrors={formErrors.errors}
          clearFieldError={formErrors.clearFieldError}
          resetErrors={formErrors.resetErrors}
          readOnly={!permissions?.update}
        />
      );
    }

    return null;
  };

  const handleTabClick = (tab: ModalCustomizedTabs) => {
    if (tab === ModalCustomizedTabs.items || tab === ModalCustomizedTabs.locales) {
      if (!hasSelectedGeo) {
        dispatch(
          showNotification({
            title: t("warning"),
            message: t("selectGeoFirst"),
            type: NotificationTypes.warning,
          }),
        );
        return;
      }

      if (!hasMainData) {
        dispatch(
          showNotification({
            title: t("warning"),
            message: t("saveMainDataFirst"),
            type: NotificationTypes.warning,
          }),
        );
        return;
      }
    }

    if (tab !== activeTab) {
      setLocaleReady(false);
      formErrors.resetErrors();
    }
    setActiveTab(tab);
  };

  const isLoaded = () => {
    if (isEditing && !isItemFetched) {
      return false;
    }

    return true;
  };

  return (
    <Modal
      size="sm"
      onClose={handleCloseClick}
      className={styles.customizedModal}
    >
      <Modal.Header>{t("customized")}</Modal.Header>

      {!isLoaded() ? (
        <Modal.Content>
          <ModalSkeleton />
        </Modal.Content>
      ) : (
        <>
          <Modal.Tabs
            tabs={tabs}
            activeTab={activeTab}
            onClick={handleTabClick}
          />

          <Modal.Content
            className={`${styles.customizedContent} ${
              activeTab === ModalCustomizedTabs.locales ? styles.localesContent : ""
            }`}
          >
            {activeTab === ModalCustomizedTabs.main && (
              <div className={styles.tabsGeo}>
                <Modal.Tabs
                  tabs={geoList}
                  activeTab={payload?.geo}
                  onClick={(geo) => {
                    dispatch(
                      handleUpdateFields({
                        geo: payload?.geo?.includes(geo)
                          ? payload.geo.filter((item) => item !== geo)
                          : [...(payload?.geo ?? []), geo],
                      }),
                    );
                  }}
                  isGeoTabs={true}
                  isSmallModifier={true}
                />
              </div>
            )}

            {renderTab()}
          </Modal.Content>

          {permissions?.update && (
            <Modal.Footer>
              {(activeTab === ModalCustomizedTabs.items &&
                !isCustomWidgetItemsFetched) ||
              (activeTab === ModalCustomizedTabs.locales && !localeReady) ? (
                <ModalFooterSkeleton />
              ) : (
                <div className={styles.footerActions}>
                  <Button
                    isSecondary
                    txt={t("cancel")}
                    handleClick={handleCloseClick}
                  />

                  <Button
                    isPrimary
                    txt={t("save")}
                    handleClick={handleApply}
                    isLoad={isItemFetching}
                    disabled={
                      activeTab === ModalCustomizedTabs.locales && !localeReady
                    }
                  />
                </div>
              )}
            </Modal.Footer>    
          )}
        </>
      )}
    </Modal>
  );
};

export default CmsCustomizedEditModal;
